import { useColorScheme, useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import React from 'react';

import { MAREX_WATERMARK, MAREX_WATERMARK_DARK } from '../../config';

import type { ReactNode as Children } from 'react';
import { getConcretePalette } from '../../utils/theme.utils';

interface StyleProps {
    watermarkPrintBackgroundImage: string;
    watermarkBackgroundImage: string;
}
interface WatermarkProps {
    children: Children;
    watermarkText?: string;
}

const getAngle = (degrees: number): number => {
    return (degrees * Math.PI) / 180;
};

const createWatermark = (
    watermarkText: string | undefined,
    contextFillStyle: string,
    contextFont: string,
    canvasWidth: number,
    canvasHeight: number,
    translateX: number,
    translateY: number,
    fillTextX: number,
    fillTextY: number,
    contextFillTextMaxWidth?: number
): string | undefined => {
    if (!watermarkText) return;

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.translate(translateX, translateY);
    ctx.rotate(getAngle(45));
    ctx.fillStyle = contextFillStyle;
    ctx.font = contextFont;

    contextFillTextMaxWidth
        ? ctx.fillText(
              watermarkText,
              fillTextX,
              fillTextY,
              contextFillTextMaxWidth
          )
        : ctx.fillText(watermarkText, fillTextX, fillTextY);

    return canvas.toDataURL();
};

export const Watermark = ({ children, watermarkText }: WatermarkProps) => {
    const theme = useTheme();
    const { mode, systemMode } = useColorScheme();
    const palette = getConcretePalette(theme, mode, systemMode);
    const fontFamily: string | undefined = theme.typography.fontFamily;

    const pageEmailWatermark = createWatermark(
        watermarkText,
        palette.background.paper,
        `30px ${fontFamily}`,
        (watermarkText || '').length * 15,
        (watermarkText || '').length * 15,
        10,
        10,
        0,
        0
    );

    const printableEmailWatermark = createWatermark(
        watermarkText,
        palette.text.primary,
        `50px ${fontFamily}`,
        300,
        300,
        0,
        10,
        10,
        10,
        300
    );

    const watermarks: StyleProps = {
        watermarkPrintBackgroundImage:
            printableEmailWatermark ?? MAREX_WATERMARK_DARK,
        watermarkBackgroundImage: pageEmailWatermark ?? MAREX_WATERMARK,
    };

    return (
        <Box
            sx={{
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    zIndex: -1,
                    opacity: 0.1,
                    backgroundImage: `url('${watermarks.watermarkBackgroundImage}')`,
                    backgroundRepeat: 'repeat',
                },
                '@media print': {
                    '& .watermark-print': { visibility: 'visible' },
                },
            }}
        >
            <Box
                className="watermark-print"
                sx={{
                    visibility: 'hidden',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: 0.1,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url('${watermarks.watermarkPrintBackgroundImage}')`,
                    backgroundSize: '100%',
                    backgroundRepeat: 'repeat',
                    zIndex: 999,
                    inset: 0,
                }}
            />
            {children}
        </Box>
    );
};

export default Watermark;
